import { NavLink } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
// mantine
import { Badge, Table, Text, UnstyledButton } from '@mantine/core';
// icons
import { Copy, Trash2 } from 'lucide-react';
// components
import { PopoverButton } from '@newo/ui-kit';
import { IFlow } from '@newo/api/gateway';
import DateTime from '@/modules/DateTime/components/DateTime';
import DuplicateFlowForm from '@/pages/Agents/components/DuplicateFlowForm';
// utils
import { cn } from '@/utils';
import { useDeleteFlowMutation } from '@/services/designer/flow/flow/flow.queries.ts';
import { QUERY_KEYS as BFF_QUERY_KEYS } from '@/services/bff/bff.queries.ts';
import { openConfirmDeleteModal } from '@/utils/ui.tsx';
// assets
import styles from '@/pages/Agents/components/FlowsTable/components/FlowRow/FlowRow.module.css';

interface FlowRowProps {
    flow: IFlow;
}

const FlowRow = ({ flow }: FlowRowProps) => {
    const queryClient = useQueryClient();
    const deleteMutation = useDeleteFlowMutation();
    const isPublished = Boolean(flow.publication_datetime);

    const handleDelete = () => {
        openConfirmDeleteModal({
            title: `Delete ${flow.idn} flow?`,
            children: `Are you sure you want to delete ${flow.idn} flow?`,
            confirmLabel: 'Delete Flow',
            onConfirm: () => {
                deleteMutation.mutate(
                    { flowId: flow.id },
                    {
                        onSuccess: () => {
                            queryClient.invalidateQueries({
                                queryKey: BFF_QUERY_KEYS.getAgentsList()
                            });
                        }
                    }
                );
            }
        });
    };

    const onDuplicate = () => {
        queryClient.invalidateQueries({
            queryKey: BFF_QUERY_KEYS.getAgentsList()
        });
    };

    return (
        <Table.Tr className={cn('rowFadeIn', styles.flow)}>
            <Table.Td className="align-top">
                <NavLink
                    className="font-medium break-all text-black"
                    to={`/agent/${flow.id}/builder`}
                >
                    {flow.title}
                </NavLink>
                <Text c="dimmed" className="break-all" fw={500} fz="xs">
                    Idn: {flow.idn}
                </Text>
            </Table.Td>
            <Table.Td>
                <div className="break-all">{flow.description}</div>
            </Table.Td>
            <Table.Td>
                {flow.creation_datetime ? (
                    <DateTime
                        format="yyyy-LL-dd HH:mm a"
                        value={flow.creation_datetime}
                    />
                ) : (
                    '-'
                )}
            </Table.Td>
            <Table.Td>
                {flow.last_change_datetime ? (
                    <DateTime
                        format="yyyy-LL-dd HH:mm a"
                        value={flow.last_change_datetime}
                    />
                ) : (
                    '-'
                )}
            </Table.Td>
            <Table.Td>
                {flow.publication_datetime ? (
                    <DateTime
                        format="yyyy-LL-dd HH:mm a"
                        value={flow.publication_datetime}
                    />
                ) : (
                    '-'
                )}
            </Table.Td>
            <Table.Td>
                <Badge
                    className="capitalize"
                    color={isPublished ? 'green' : 'gray'}
                    variant={isPublished ? 'filled' : 'default'}
                >
                    {isPublished ? 'Published' : 'Unpublished'}
                </Badge>
            </Table.Td>
            <Table.Td>
                <div className="flex justify-end gap-4">
                    <UnstyledButton
                        className="leading-none"
                        title="Delete flow"
                        onClick={handleDelete}
                    >
                        <Trash2 />
                    </UnstyledButton>
                    <PopoverButton
                        className="leading-none"
                        title="Duplicate flow"
                        trigger={<Copy />}
                    >
                        {(close) => (
                            <DuplicateFlowForm
                                agentId={flow.agent_id}
                                flowId={flow.id}
                                onCancel={close}
                                onSubmit={onDuplicate}
                            />
                        )}
                    </PopoverButton>
                </div>
            </Table.Td>
        </Table.Tr>
    );
};

export default FlowRow;
